import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, Subject } from 'rxjs';
import { HashCodeBuilder } from 'src/lib/fliko/helpers/hashCodeBuilder';
import { WebPushSubscriptionData } from 'src/lib/fliko/models/browserNtf/browserNtf.data';
import {
  BrowserNtfToastComponent,
  BrowserNtfToastData
} from 'src/app/shared/components/browser-notification-toast/browser-ntf-toast.component';
import { Sound, SoundService } from '../sound.service';
import { BrowserNtfNavigationService } from './browser-ntf-navigation.service';
import { BrowserNtf } from './browser-ntf.constants';
import { BrowserNtfSwMessageData, BrowserNtfSyncPushOptions } from './browser-ntf.models';

@Injectable({ providedIn: 'root' })
export class BrowserNtfPushRuntimeService {
  private readonly recentNotifications = new Map<string, number>();
  private readonly pushReceivedSubject = new Subject<BrowserNtfSwMessageData>();
  private readonly notificationOpenedSubject = new Subject<string>();

  public readonly pushReceived$: Observable<BrowserNtfSwMessageData> = this.pushReceivedSubject.asObservable();
  public readonly notificationOpened$: Observable<string> = this.notificationOpenedSubject.asObservable();

  private swMessageAttached = false;
  private swRegistration: ServiceWorkerRegistration | null = null;
  private swRegistrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;

  constructor(
    private matSnackBar: MatSnackBar,
    private soundService: SoundService,
    private navigationSrv: BrowserNtfNavigationService
  ) {}

  /**
   * Initializes SW runtime listeners once.
   * It is safe to call repeatedly; re-initialization is ignored.
   * @return {void}
   */
  public initializeRuntime(): void {
    this.attachServiceWorkerMessageListener();
    void this.ensureServiceWorkerRegistration();
  }

  /**
   * Creates or refreshes Web Push subscription in browser and returns payload that should be persisted in backend by higher-level
   * service.
   * @param {string}                                userId
   * @param {string}                                vapidPublicKey
   * @param {BrowserNtfSyncPushOptions | undefined} options
   * @return {Promise<WebPushSubscriptionData | null>}
   */
  public async syncPushSubscription(
    userId: string,
    vapidPublicKey: string,
    options?: BrowserNtfSyncPushOptions
  ): Promise<WebPushSubscriptionData | null> {
    if (!this.supportsPushManager() || !vapidPublicKey || !userId) return null;

    const registration = await this.ensureServiceWorkerRegistration();
    if (!registration) return null;

    let subscription = await registration.pushManager.getSubscription();
    if (options?.forceResubscribe && subscription) {
      try {
        await subscription.unsubscribe();
      } catch {
        // Continue with existing subscription when browser refuses unsubscribe.
      }
      subscription = null;
    }

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToArrayBuffer(vapidPublicKey)
      });
    }

    const data = subscription.toJSON();
    const endpoint = data.endpoint ?? subscription.endpoint;
    if (!endpoint) return null;

    const id = this.getSubscriptionId(subscription);
    const now = Date.now();

    return {
      id,
      userId,
      endpoint,
      expirationTime: data.expirationTime ?? null,
      keys: {
        auth: data.keys?.auth ?? '',
        p256dh: data.keys?.p256dh ?? ''
      },
      language: navigator.language,
      userAgent: navigator.userAgent,
      createDate: now,
      lastUpdate: now
    };
  }

  /**
   * Checks whether current browser has an active Push API subscription.
   * @return {Promise<boolean>}
   */
  public async hasPushSubscription(): Promise<boolean> {
    const registration = await this.ensureServiceWorkerRegistration();
    if (!registration) return false;

    return !!(await registration.pushManager.getSubscription());
  }

  /**
   * Unsubscribes current browser from Push API and returns local subscription id used in DB.
   * @return {Promise<string | null>}
   */
  public async unsubscribePushSubscription(): Promise<string | null> {
    const registration = await this.ensureServiceWorkerRegistration();
    if (!registration) return null;

    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return null;

    const id = this.getSubscriptionId(subscription);
    try {
      await subscription.unsubscribe();
    } catch {
      // DB binding is still removed by caller so this browser stops receiving server-side pushes.
    }

    return id;
  }

  /**
   * Checks browser capability for service workers.
   * @return {boolean}
   */
  private supportsServiceWorker(): boolean {
    return typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
  }

  /**
   * Checks browser capability for Push API.
   * @return {boolean}
   */
  private supportsPushManager(): boolean {
    return this.supportsServiceWorker() && 'PushManager' in window;
  }

  /**
   * Builds stable DB id from Push API subscription endpoint.
   * @param {PushSubscription} subscription
   * @return {string}
   */
  private getSubscriptionId(subscription: PushSubscription): string {
    return `sub_${Math.abs(new HashCodeBuilder(subscription.endpoint).valueOf())}`;
  }

  /**
   * Prevents duplicate in-app handling of the same notification id within configured deduplication window.
   * @param {string} notificationId
   * @param {number} dedupeMs
   * @return {boolean}
   */
  private shouldSkip(notificationId: string, dedupeMs: number): boolean {
    const now = Date.now();
    this.recentNotifications.forEach((timestamp, id) => {
      if (now - timestamp >= dedupeMs) this.recentNotifications.delete(id);
    });
    const previous = this.recentNotifications.get(notificationId);
    if (previous != null && now - previous < dedupeMs) return true;
    this.recentNotifications.set(notificationId, now);
    return false;
  }

  /**
   * Attaches single listener for Service Worker `message` events and dispatches click/push payload handling.
   * @return {void}
   */
  private attachServiceWorkerMessageListener(): void {
    if (!this.supportsServiceWorker() || this.swMessageAttached) return;

    navigator.serviceWorker.addEventListener(BrowserNtf.SW_MESSAGE_EVENT, (event: MessageEvent<BrowserNtfSwMessageData>) => {
      const payload = event.data;
      if (!payload) return;

      if (payload.type === BrowserNtf.SW_EVENT_CLICK) {
        if (payload.notificationId) this.notificationOpenedSubject.next(payload.notificationId);
        this.navigationSrv.navigateToNotificationUrl(payload.url, payload.estateId ?? undefined);
        return;
      }

      if (payload.type !== BrowserNtf.SW_EVENT_PUSH) return;

      const title = payload.title ?? '';
      if (!title) return;

      if (payload.notificationId && this.shouldSkip(payload.notificationId, BrowserNtf.DEFAULT_DEDUPE_MS)) {
        return;
      }

      this.pushReceivedSubject.next(payload);

      if (payload.playSound !== false) {
        void this.soundService.play(Sound.PING);
      }
      this.showInAppToast({
        title,
        body: payload.body ?? '',
        url: payload.url ?? null,
        estateId: payload.estateId ?? null,
        estateName: payload.estateName ?? null,
        notificationId: payload.notificationId ?? null,
        onOpenDetails: (notificationId) => this.notificationOpenedSubject.next(notificationId),
        createDate: payload.createDate
      });
    });

    this.swMessageAttached = true;
  }

  /**
   * Ensures browser has active registration for push notification Service Worker.
   * Registration is cached in memory after first successful call.
   * @return {Promise<ServiceWorkerRegistration | null>}
   */
  private async ensureServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
    if (!this.supportsServiceWorker()) return null;
    if (this.hasActiveServiceWorker(this.swRegistration)) return this.swRegistration;
    if (this.swRegistrationPromise) return this.swRegistrationPromise;

    this.swRegistrationPromise = this.registerActiveServiceWorker();

    try {
      const registration = await this.swRegistrationPromise;
      this.swRegistration = this.hasActiveServiceWorker(registration) ? registration : null;
      return registration;
    } finally {
      this.swRegistrationPromise = null;
    }
  }

  /**
   * Registers push Service Worker and waits until browser exposes active worker
   * required by `PushManager.subscribe`.
   * @return {Promise<ServiceWorkerRegistration | null>}
   */
  private async registerActiveServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    try {
      const registration = await navigator.serviceWorker.register(BrowserNtf.SW_PATH);
      const activeRegistration = await this.waitForServiceWorkerActivation(registration);
      return this.hasActiveServiceWorker(activeRegistration) ? activeRegistration : null;
    } catch (error) {
      console.warn('Service worker registration failed', error);
      return null;
    }
  }

  /**
   * Waits until registration has active worker. Fresh registrations may resolve
   * from `register()` before activation completes, which would make
   * `pushManager.subscribe()` fail with `AbortError`.
   * @param {ServiceWorkerRegistration} registration
   * @return {Promise<ServiceWorkerRegistration | null>}
   */
  private async waitForServiceWorkerActivation(
    registration: ServiceWorkerRegistration
  ): Promise<ServiceWorkerRegistration | null> {
    if (registration.active) return registration;

    const worker = registration.installing ?? registration.waiting;
    if (worker) {
      await new Promise<void>((resolve) => {
        const onStateChange = (): void => {
          if (worker.state === 'activated' || worker.state === 'redundant') {
            worker.removeEventListener('statechange', onStateChange);
            resolve();
          }
        };

        worker.addEventListener('statechange', onStateChange);
        onStateChange();
      });
    }

    if (registration.active) return registration;

    const readyRegistration = await navigator.serviceWorker.ready;
    if (this.isSameServiceWorkerScope(readyRegistration, registration) && this.hasActiveServiceWorker(readyRegistration)) {
      return readyRegistration;
    }

    return registration.active ? registration : null;
  }

  /**
   * Checks whether registration already exposes active worker instance.
   * @param {ServiceWorkerRegistration | null | undefined} registration
   * @return {boolean}
   */
  private hasActiveServiceWorker(
    registration: ServiceWorkerRegistration | null | undefined
  ): registration is ServiceWorkerRegistration {
    return !!registration?.active;
  }

  /**
   * Compares Service Worker scopes to ensure fallback registration from
   * `navigator.serviceWorker.ready` belongs to the same worker file.
   * @param {ServiceWorkerRegistration | null | undefined} first
   * @param {ServiceWorkerRegistration | null | undefined} second
   * @return {boolean}
   */
  private isSameServiceWorkerScope(
    first: ServiceWorkerRegistration | null | undefined,
    second: ServiceWorkerRegistration | null | undefined
  ): boolean {
    return !!first && !!second && first.scope === second.scope;
  }

  /**
   * Displays in-app toast corresponding to incoming browser notification.
   * @param {BrowserNtfToastData & { url: string | null }} data
   * @return {void}
   */
  private showInAppToast(data: BrowserNtfToastData & { url: string | null }): void {
    this.matSnackBar.dismiss();
    this.matSnackBar.openFromComponent(BrowserNtfToastComponent, {
      duration: BrowserNtf.TOAST_DURATION_MS,
      horizontalPosition: BrowserNtf.TOAST_HORIZONTAL_POSITION,
      verticalPosition: BrowserNtf.TOAST_VERTICAL_POSITION,
      panelClass: [BrowserNtf.TOAST_PANEL_CLASS],
      data
    });
  }

  /**
   * Converts VAPID public key from URL-safe base64 format into byte array required by `PushManager.subscribe`.
   * @param {string} base64String
   * @return {ArrayBuffer}
   */
  private urlBase64ToArrayBuffer(base64String: string): ArrayBuffer {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = atob(base64);
    const buffer = new ArrayBuffer(rawData.length);
    const outputArray = new Uint8Array(buffer);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }

    return buffer;
  }
}
